import React, { useState, useEffect } from "react";
import { Search, Filter, Download, Plus, RefreshCcw, CircleDollarSign } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getAllVouchers, Voucher } from "@/lib/db/voucherDb";

const AllVouchersTab: React.FC = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    memberType: "all",
    voucherType: "all",
    status: "all",
    startDate: "",
    endDate: ""
  });
  const { toast } = useToast();

  useEffect(() => {
    loadVouchers();
  }, []);

  const loadVouchers = async () => {
    setIsLoading(true);
    try {
      const allVouchers = await getAllVouchers();
      setVouchers(allVouchers);
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading vouchers:", error);
      toast({
        variant: "destructive",
        title: "Failed to load vouchers",
        description: "Could not retrieve voucher data. Please try again."
      });
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleExportCSV = () => {
    // Implementation for CSV export would go here
    toast({
      title: "Export started",
      description: "Voucher data is being exported as CSV."
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      memberType: "all",
      voucherType: "all",
      status: "all",
      startDate: "",
      endDate: ""
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "Active":
        return "default";
      case "Expired":
        return "destructive";
      case "Used":
        return "secondary";
      case "Pending":
        return "outline";
      default:
        return "default";
    }
  };

  const filteredVouchers = vouchers.filter(voucher => {
    // Apply search filter
    const searchFields = [
      voucher.customerName.toLowerCase(),
      voucher.icNumber.toLowerCase(),
      voucher.mobileNumber.toLowerCase(),
      voucher.voucherCode.toLowerCase()
    ];
    
    const matchesSearch = searchTerm === "" || 
      searchFields.some(field => field.includes(searchTerm.toLowerCase()));
    
    // Apply other filters
    const matchesMemberType = filters.memberType === "all" || voucher.memberType === filters.memberType;
    const matchesVoucherType = filters.voucherType === "all" || voucher.voucherType === filters.voucherType;
    const matchesStatus = filters.status === "all" || voucher.status === filters.status;
    
    // Apply date filters
    let matchesDates = true;
    if (filters.startDate) {
      const startDate = new Date(filters.startDate).getTime();
      matchesDates = matchesDates && voucher.issueDate >= startDate;
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate).getTime() + 86400000; // Add one day to include the end date fully
      matchesDates = matchesDates && voucher.expiryDate <= endDate;
    }
    
    return matchesSearch && matchesMemberType && matchesVoucherType && matchesStatus && matchesDates;
  });

  const VoucherBalanceDialog = ({ voucher }: { voucher: Voucher }) => {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm"
            className="w-8 h-8 p-0 flex items-center justify-center"
            title="Check voucher balance"
          >
            <CircleDollarSign className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Voucher Balance</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Voucher Code</Label>
                  <div className="text-base">{voucher.voucherCode}</div>
                </div>
                <div>
                  <Label>Customer Name</Label>
                  <div className="text-base">{voucher.customerName}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Original Amount</Label>
                  <div className="text-base">RM {voucher.voucherAmount.toFixed(2)}</div>
                </div>
                <div>
                  <Label>Current Balance</Label>
                  <div className="text-lg font-bold text-green-600">
                    RM {voucher.remainingBalance.toFixed(2)}
                  </div>
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <div>
                  <Badge variant={getStatusBadgeVariant(voucher.status) as "default" | "destructive" | "secondary" | "outline"}>
                    {voucher.status}
                  </Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Issue Date</Label>
                  <div>{new Date(voucher.issueDate).toLocaleDateString()}</div>
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <div>{new Date(voucher.expiryDate).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="space-y-4">
      {/* Search and Filters Bar */}
      <div className="flex flex-col md:flex-row justify-between gap-4 items-start md:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, IC, phone or voucher code..."
            className="pl-8"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={loadVouchers}
          >
            <RefreshCcw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Assign New Voucher
          </Button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-muted/40 p-4 rounded-md border border-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="memberType">Member Type</Label>
              <select
                id="memberType"
                className="w-full border border-input rounded-md h-10 px-3 bg-background"
                value={filters.memberType}
                onChange={(e) => handleFilterChange("memberType", e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="Customer">Customer</option>
                <option value="Coop Member">Coop Member</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="voucherType">Voucher Type</Label>
              <select
                id="voucherType"
                className="w-full border border-input rounded-md h-10 px-3 bg-background"
                value={filters.voucherType}
                onChange={(e) => handleFilterChange("voucherType", e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="Food Aid">Food Aid</option>
                <option value="School Supplies">School Supplies</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Utility Payments">Utility Payments</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                className="w-full border border-input rounded-md h-10 px-3 bg-background"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Used">Used</option>
                <option value="Expired">Expired</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="startDate">Issue Date From</Label>
                <Input
                  type="date"
                  id="startDate"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange("startDate", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="endDate">To</Label>
                <Input
                  type="date"
                  id="endDate"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      )}
      
      {/* Vouchers Table */}
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer Name</TableHead>
              <TableHead>IC Number</TableHead>
              <TableHead>Member Type</TableHead>
              <TableHead>Voucher Code</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Voucher Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Issue Date</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center">
                  Loading vouchers...
                </TableCell>
              </TableRow>
            ) : filteredVouchers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center">
                  No vouchers found
                </TableCell>
              </TableRow>
            ) : (
              filteredVouchers.map((voucher) => (
                <TableRow key={voucher.voucherId}>
                  <TableCell className="font-medium">{voucher.customerName}</TableCell>
                  <TableCell>{voucher.icNumber}</TableCell>
                  <TableCell>{voucher.memberType}</TableCell>
                  <TableCell className="font-mono">{voucher.voucherCode}</TableCell>
                  <TableCell>RM {voucher.voucherAmount.toFixed(2)}</TableCell>
                  <TableCell>{voucher.voucherType}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(voucher.status) as "default" | "destructive" | "secondary" | "outline"}>
                      {voucher.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(voucher.issueDate).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(voucher.expiryDate).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <VoucherBalanceDialog voucher={voucher} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* No vouchers message */}
      {!isLoading && vouchers.length === 0 && (
        <div className="text-center p-8">
          <p className="text-muted-foreground mb-4">No vouchers have been created yet.</p>
          <Button>Assign Your First Voucher</Button>
        </div>
      )}
    </div>
  );
};

export default AllVouchersTab;
